import {
    type ApiEndpoint,
    type DownloadUrlResponse,
    isDownloadUrlResponse,
} from "@/shared/api-endpoint";
import type { ParameterRange } from "@/shared/parameter-range";
import { isObject } from "@/shared/utils/typeGuards";
import type { Variations30RequestBody } from "@/shared/variations-3.0.api";

// SpliceGen 3.0 conditioned on SLAP instead of CL-SiSo, with the SLAP embedding
// editable through a sparse autoencoder's concepts. Feature ids are only
// meaningful against the SAE bundle the server reports as `version`.

export const kVariations30SlapRoute = "/variations-3.0-slap";
export const kSlapConceptsDecomposeRoute = "/slap-concepts/decompose";
export const kSlapConceptsSearchRoute = "/slap-concepts/search";

export const kSlapBlendModes = ["relative", "convex", "additive"] as const;
export type SlapBlendMode = (typeof kSlapBlendModes)[number];
export const kSlapBlendDefault: SlapBlendMode = "relative";

// Global edit strength. 0 decodes the loop's own concepts unchanged, 1 adds the
// recipe at the loop's own concept magnitude, negatives steer away from it.
export const kSlapAlphaRange: ParameterRange = { min: -1, max: 1.5, default: 1 };
export const kSlapConceptWeightRange: ParameterRange = { min: -1, max: 1, default: 0 };
export const kSlapMaxRecipeLength = 25;
export const kSlapDecomposeKRange: ParameterRange = { min: 1, max: 50, default: 10 };
export const kSlapSearchLimitRange: ParameterRange = { min: 1, max: 100, default: 12 };

/** A Splice sample near the concept's direction, for auditioning it. */
export interface SlapConceptExample {
    uuid: string;
    name: string | null;
    pack: string | null;
    score: number;
}

export interface SlapConcept {
    feature: number;
    label: string;
    caption: string;
    density: number | null;
    verified: boolean;
    /** How strongly the decomposed loop activates it; absent on search results. */
    activation?: number;
    /** `activation` as a share of the concept's strongest activation in the corpus. */
    relative?: number;
    /** Empty when the bundle has no baked examples. */
    examples?: SlapConceptExample[];
}

export interface SlapRecipeEntry {
    feature: number;
    weight: number;
}

export interface Variations30SlapRequestBody extends Variations30RequestBody {
    /** Omitted or empty generates from the loop's own SLAP embedding. */
    recipe?: SlapRecipeEntry[];
    alpha?: number;
    blend?: SlapBlendMode;
}

export interface SlapConceptsDecomposeRequestBody {
    fileKey: string;
    k?: number;
}

export interface SlapConceptsSearchRequestBody {
    query: string;
    limit?: number;
    verifiedOnly?: boolean;
}

export interface SlapConceptsResponse {
    ok: true;
    version: string;
    features: SlapConcept[];
    /** Decompose only: verified concepts the loop nearly activates. */
    suggested?: SlapConcept[];
}

export const isSlapBlendMode = (value: unknown): value is SlapBlendMode =>
    typeof value === "string" && (kSlapBlendModes as readonly string[]).includes(value);

const isSlapRecipeEntry = (value: unknown): value is SlapRecipeEntry =>
    isObject(value) && typeof value.feature === "number" && typeof value.weight === "number";

const isNullableString = (value: unknown): value is string | null =>
    value === null || typeof value === "string";

const isSlapConceptExample = (value: unknown): value is SlapConceptExample =>
    isObject(value) &&
    typeof value.uuid === "string" &&
    isNullableString(value.name) &&
    isNullableString(value.pack) &&
    typeof value.score === "number";

export const isSlapConcept = (value: unknown): value is SlapConcept =>
    isObject(value) &&
    typeof value.feature === "number" &&
    typeof value.label === "string" &&
    typeof value.caption === "string" &&
    (value.density === null || typeof value.density === "number") &&
    typeof value.verified === "boolean" &&
    (value.activation === undefined || typeof value.activation === "number") &&
    (value.relative === undefined || typeof value.relative === "number") &&
    (value.examples === undefined ||
        (Array.isArray(value.examples) && value.examples.every(isSlapConceptExample)));

export const isVariations30SlapRequestBody = (
    value: unknown,
): value is Variations30SlapRequestBody =>
    isObject(value) &&
    typeof value.fileKey === "string" &&
    typeof value.bpm === "number" &&
    (value.recipe === undefined ||
        (Array.isArray(value.recipe) && value.recipe.every(isSlapRecipeEntry))) &&
    (value.alpha === undefined || typeof value.alpha === "number") &&
    (value.blend === undefined || isSlapBlendMode(value.blend));

export const isSlapConceptsDecomposeRequestBody = (
    value: unknown,
): value is SlapConceptsDecomposeRequestBody =>
    isObject(value) &&
    typeof value.fileKey === "string" &&
    (value.k === undefined || typeof value.k === "number");

export const isSlapConceptsSearchRequestBody = (
    value: unknown,
): value is SlapConceptsSearchRequestBody =>
    isObject(value) &&
    typeof value.query === "string" &&
    (value.limit === undefined || typeof value.limit === "number") &&
    (value.verifiedOnly === undefined || typeof value.verifiedOnly === "boolean");

export const isSlapConceptsResponse = (value: unknown): value is SlapConceptsResponse =>
    isObject(value) &&
    value.ok === true &&
    typeof value.version === "string" &&
    Array.isArray(value.features) &&
    value.features.every(isSlapConcept) &&
    (value.suggested === undefined ||
        (Array.isArray(value.suggested) && value.suggested.every(isSlapConcept)));

export const kVariations30SlapEndpoint: ApiEndpoint<
    Variations30SlapRequestBody,
    DownloadUrlResponse
> = {
    route: kVariations30SlapRoute,
    label: "Variations 3.0 SLAP",
    source: "Modal",
    isEvaluationOnly: true,
    category: "SpliceGen v3.0 SLAP",
    description:
        "Variations using SpliceGen 3.0 conditioned on a SLAP embedding, optionally steered along SAE concepts.",
    isRequestBody: isVariations30SlapRequestBody,
    isSuccessResponse: isDownloadUrlResponse,
};

export const kSlapConceptsDecomposeEndpoint: ApiEndpoint<
    SlapConceptsDecomposeRequestBody,
    SlapConceptsResponse
> = {
    route: kSlapConceptsDecomposeRoute,
    label: "SLAP Concepts Decompose",
    source: "Modal",
    isEvaluationOnly: true,
    category: "SpliceGen v3.0 SLAP",
    description:
        "The SAE concepts a loop's SLAP embedding activates, most telling first, plus near misses to suggest.",
    isRequestBody: isSlapConceptsDecomposeRequestBody,
    isSuccessResponse: isSlapConceptsResponse,
};

export const kSlapConceptsSearchEndpoint: ApiEndpoint<
    SlapConceptsSearchRequestBody,
    SlapConceptsResponse
> = {
    route: kSlapConceptsSearchRoute,
    label: "SLAP Concepts Search",
    source: "Modal",
    isEvaluationOnly: true,
    category: "SpliceGen v3.0 SLAP",
    description: "SAE concepts whose label or caption matches a text query.",
    isRequestBody: isSlapConceptsSearchRequestBody,
    isSuccessResponse: isSlapConceptsResponse,
};
